(() => {
  const ROOT_SELECTOR = '[data-funny-product-page]';

  class FunnyProductPage {
    constructor(root) {
      this.root = root;
      this.currency = root.dataset.currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
      this.locale = root.dataset.locale || document.documentElement.lang || navigator.language || 'en-US';
      this.variants = this.readVariants();

      const positiveVariantPrices = this.variants
        .map(variant => Number(variant.price || 0))
        .filter(price => price > 0);

      this.basePrice =
        Number(root.dataset.basePrice || 0) ||
        (positiveVariantPrices.length ? Math.min(...positiveVariantPrices) : 0);

      this.currentVariant =
        this.variants.find(v => String(v.id) === String(this.root.querySelector('[data-variant-id]')?.value)) ||
        this.variants[0] ||
        null;

      this.bundleQty = 1;
      this.selection = [];
      this.hasVariantCards = Boolean(this.root.querySelector('[data-variant-card]'));
      this.viewingTimer = null;
      this.lastViewerCount = null;
      this.selectionErrorTimer = null;
      this.stickyObserver = null;
      this.stickyCartDrawerOpen = false;
      this.stickyCartDrawerGraceUntil = 0;
      this.stickyVisibilityCleanup = null;

      this.bindGallery();
      this.bindBundles();
      this.bindAccordions();
      this.bindVariants();
      this.bindForm();
      this.bindStickyBar();
      this.bindReviewScroll();
      this.bindViewingCounter();

      this.syncSelectedBundle();
      this.updateCartQuantity();

      if (this.currentVariant) {
        this.updateStockBadge(this.currentVariant);
        this.updateCountryCard(this.currentVariant);
      }

      this.updateSelectionUI();
    }

    readVariants() {
      const node = this.root.querySelector('[data-product-json]');
      if (!node) return [];

      try {
        return JSON.parse(node.textContent || '[]');
      } catch (error) {
        console.error('[FunnyProductPage] Product JSON could not be parsed:', error);
        return [];
      }
    }

    bindGallery() {
      this.root.querySelectorAll('[data-media-target]').forEach(button => {
        button.addEventListener('click', () => this.showMedia(button.dataset.mediaTarget));
      });

      const thumbs = this.root.querySelector('[data-thumbs]');
      const prev = this.root.querySelector('[data-thumb-prev]');
      const next = this.root.querySelector('[data-thumb-next]');

      if (thumbs && prev) {
        prev.addEventListener('click', () => thumbs.scrollBy({ left: -220, behavior: 'smooth' }));
      }

      if (thumbs && next) {
        next.addEventListener('click', () => thumbs.scrollBy({ left: 220, behavior: 'smooth' }));
      }

      this.bindGallerySwipe();
    }

    bindGallerySwipe() {
      const mainWrap = this.root.querySelector('.fpp-main-media-wrap');
      if (!mainWrap) return;

      let startX = 0;
      let startY = 0;

      mainWrap.addEventListener('touchstart', event => {
        if (event.touches.length !== 1) return;

        const touch = event.touches[0];
        startX = touch.clientX;
        startY = touch.clientY;
      }, { passive: true });

      mainWrap.addEventListener('touchend', event => {
        if (!window.matchMedia('(max-width: 989px)').matches) return;
        if (!event.changedTouches.length) return;

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - startX;
        const deltaY = touch.clientY - startY;
        const horizontalDistance = Math.abs(deltaX);
        const verticalDistance = Math.abs(deltaY);

        if (horizontalDistance < 42) return;
        if (horizontalDistance <= verticalDistance * 1.15) return;

        this.moveGalleryMedia(deltaX < 0 ? 1 : -1);
      }, { passive: true });
    }

    moveGalleryMedia(direction) {
      const panels = [...this.root.querySelectorAll('[data-media-panel]')];
      if (panels.length < 2) return;

      let currentIndex = panels.findIndex(panel =>
        panel.classList.contains('is-active') && !panel.hidden
      );

      if (currentIndex < 0) currentIndex = 0;

      const nextIndex = (currentIndex + direction + panels.length) % panels.length;
      const nextPanel = panels[nextIndex];
      const nextId = nextPanel?.dataset.mediaPanel;

      if (!nextId) return;

      this.showMedia(nextId);

      const thumbs = this.root.querySelector('[data-thumbs]');
      const activeThumb = this.root.querySelector(`[data-media-target="${nextId}"]`);

      if (thumbs && activeThumb) {
        const targetLeft =
          activeThumb.offsetLeft -
          (thumbs.clientWidth / 2) +
          (activeThumb.offsetWidth / 2);

        thumbs.scrollTo({
          left: Math.max(0, targetLeft),
          behavior: 'smooth',
        });
      }
    }

    showMedia(id) {
      if (!id) return;

      this.root.querySelectorAll('[data-media-panel]').forEach(panel => {
        const active = String(panel.dataset.mediaPanel) === String(id);
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });

      this.root.querySelectorAll('[data-media-target]').forEach(button => {
        const active = String(button.dataset.mediaTarget) === String(id);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    bindBundles() {
      this.root.querySelectorAll('[data-bundle-card]').forEach(card => {
        const input = card.querySelector('input[type="radio"]');
        if (!input) return;

        input.addEventListener('change', () => {
          if (input.checked) this.selectBundle(card);
        });

        card.addEventListener('click', event => {
          if (event.target === input) return;
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }

    selectBundle(card) {
      this.root.querySelectorAll('[data-bundle-card]').forEach(item => {
        item.classList.toggle('is-selected', item === card);
      });

      this.bundleQty = Math.max(1, parseInt(card.dataset.bundleQty || '1', 10));

      const bundleProperty = this.root.querySelector('[data-bundle-property]');
      if (bundleProperty) {
        bundleProperty.value = card.dataset.bundleTitle || `${this.bundleQty}-pack`;
      }

      if (this.hasVariantCards) {
        while (this.selection.length > this.bundleQty) {
          this.selection.pop();
        }
      } else if (this.currentVariant?.available) {
        this.selection = Array(this.bundleQty).fill(String(this.currentVariant.id));
      } else {
        this.selection = [];
      }

      this.updateCartQuantity();
      this.updateSelectionUI();
    }

    syncSelectedBundle() {
      const selected =
        this.root.querySelector('[data-bundle-card] input:checked')?.closest('[data-bundle-card]') ||
        this.root.querySelector('[data-bundle-card]');

      if (selected) this.selectBundle(selected);
    }

    updateCartQuantity() {
      const field = this.root.querySelector('[data-cart-quantity]');
      if (field) field.value = Math.max(1, this.bundleQty);
    }

    bindAccordions() {
      this.root.querySelectorAll('[data-accordion]').forEach(item => {
        const trigger = item.querySelector('.fpp-accordion-trigger');
        const panel = item.querySelector('.fpp-accordion-panel');
        const chevron = item.querySelector('.fpp-chevron');

        if (!trigger || !panel) return;

        trigger.addEventListener('click', () => {
          const open = trigger.getAttribute('aria-expanded') === 'true';
          trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
          panel.hidden = open;
          item.classList.toggle('is-open', !open);
          if (chevron) chevron.textContent = open ? '⌄' : '⌃';
        });
      });
    }

    bindVariants() {
      this.root.querySelectorAll('[data-variant-card]').forEach(card => {
        const variantId = String(card.dataset.variantId || '');
        const addButton = card.querySelector('[data-variant-add]');
        const plusButton = card.querySelector('[data-variant-plus]');
        const minusButton = card.querySelector('[data-variant-minus]');

        const addOne = event => {
          event?.preventDefault();
          event?.stopPropagation();
          this.changeVariantQuantity(variantId, 1);
        };

        addButton?.addEventListener('click', addOne);
        plusButton?.addEventListener('click', addOne);

        minusButton?.addEventListener('click', event => {
          event.preventDefault();
          event.stopPropagation();
          this.changeVariantQuantity(variantId, -1);
        });
      });
    }

    changeVariantQuantity(variantId, delta) {
      const variant = this.variants.find(item => String(item.id) === String(variantId));
      if (!variant || !variant.available) return;

      const id = String(variant.id);
      const currentCount = this.selection.filter(selectedId => selectedId === id).length;

      if (delta > 0) {
        if (this.selection.length >= this.bundleQty) {
          this.showSelectionError(
            this.root.dataset.selectionFullText ||
            'Your bundle is full. Remove one item before adding another.'
          );
          return;
        }

        const maxSelectable = this.getVariantMaxSelectable(variant);

        if (currentCount >= maxSelectable) {
          this.showSelectionError(
            this.root.dataset.selectionStockText ||
            'You cannot add more of this option because of its available stock.'
          );
          return;
        }

        this.selection.push(id);
        this.setVariant(variant);
      } else if (delta < 0) {
        const index = this.selection.lastIndexOf(id);
        if (index !== -1) {
          this.selection.splice(index, 1);
        }
      }

      this.hideSelectionError();
      this.updateSelectionUI();
    }

    getVariantMaxSelectable(variant) {
      if (!variant?.available) return 0;

      const tracked = Boolean(variant.inventory_management);
      const continueSelling = variant.inventory_policy === 'continue';

      if (!tracked || continueSelling) {
        return this.bundleQty;
      }

      return Math.max(0, Number(variant.inventory_quantity || 0));
    }

    getSelectionCounts() {
      return this.selection.reduce((counts, variantId) => {
        counts.set(variantId, (counts.get(variantId) || 0) + 1);
        return counts;
      }, new Map());
    }

    getBundleCompareTotal(qty) {
      const bundleQty = Math.max(1, Number(qty || 1));
      const basePrice = Math.max(0, Number(this.basePrice || 0));

      let total = basePrice * bundleQty;

      this.selection.slice(0, bundleQty).forEach(variantId => {
        const variant = this.variants.find(
          item => String(item.id) === String(variantId)
        );

        if (!variant) return;

        const variantPrice = Math.max(basePrice, Number(variant.price || basePrice));
        total += variantPrice - basePrice;
      });

      return Math.round(total);
    }

    updateSelectionUI() {
      const counts = this.getSelectionCounts();
      const selectedTotal = this.selection.length;
      const remaining = Math.max(0, this.bundleQty - selectedTotal);
      const bundleIsFull = selectedTotal >= this.bundleQty;

      this.root.querySelectorAll('[data-variant-card]').forEach(card => {
        const variantId = String(card.dataset.variantId || '');
        const variant = this.variants.find(item => String(item.id) === variantId);
        const count = counts.get(variantId) || 0;
        const maxSelectable = variant ? this.getVariantMaxSelectable(variant) : 0;

        const countNode = card.querySelector('[data-variant-count]');
        const badge = card.querySelector('[data-variant-badge]');
        const badgeCount = card.querySelector('[data-variant-badge-count]');
        const plus = card.querySelector('[data-variant-plus]');
        const minus = card.querySelector('[data-variant-minus]');
        const preview = card.querySelector('[data-variant-add]');

        card.classList.toggle('is-selected', count > 0);

        if (countNode) countNode.textContent = String(count);

        if (badge) badge.hidden = count <= 0;
        if (badgeCount) badgeCount.textContent = String(count);

        if (minus) minus.disabled = count <= 0;

        const cannotAdd =
          !variant?.available ||
          bundleIsFull ||
          count >= maxSelectable;

        if (plus) plus.disabled = cannotAdd;
        if (preview) preview.disabled = !variant?.available || bundleIsFull || count >= maxSelectable;
      });

      const progress = this.root.querySelector('[data-selection-progress]');
      if (progress) {
        progress.textContent = `${selectedTotal} / ${this.bundleQty}`;
        progress.classList.toggle('is-complete', selectedTotal === this.bundleQty);
      }

      const helper = this.root.querySelector('[data-selection-help]');
      if (helper) {
        const template = helper.dataset.template || helper.textContent || '';
        if (!helper.dataset.template) helper.dataset.template = template;

        helper.textContent = helper.dataset.template
          .replaceAll('[count]', String(this.bundleQty))
          .replaceAll('[remaining]', String(remaining))
          .replaceAll('[total]', String(this.bundleQty));
      }

      const button = this.root.querySelector('[data-add-button]');
      const buttonText = this.root.querySelector('[data-add-button-text]');

      if (button && button.getAttribute('aria-busy') !== 'true') {
        const anyAvailable = this.variants.some(variant => variant.available);
        button.disabled = !anyAvailable || selectedTotal !== this.bundleQty;

        if (buttonText) {
          const addText = buttonText.dataset.addText || 'Add to cart';
          const soldText = buttonText.dataset.soldText || 'Sold out';

          if (!anyAvailable) {
            buttonText.textContent = soldText;
          } else if (remaining > 0) {
            const chooseTemplate = this.root.dataset.chooseMoreTemplate || 'Choose [count] more';
            buttonText.textContent = chooseTemplate.replaceAll('[count]', String(remaining));
          } else {
            buttonText.textContent = addText;
          }
        }
      }

      this.updateBundlePrices();
      this.updateStickyBar();
    }

    showSelectionError(message) {
      const node = this.root.querySelector('[data-selection-error]');
      if (!node) return;

      window.clearTimeout(this.selectionErrorTimer);
      node.textContent = message;
      node.hidden = false;

      this.selectionErrorTimer = window.setTimeout(() => {
        this.hideSelectionError();
      }, 2600);
    }

    hideSelectionError() {
      const node = this.root.querySelector('[data-selection-error]');
      if (!node) return;
      node.hidden = true;
    }

    setVariant(variant) {
      this.currentVariant = variant;

      const idField = this.root.querySelector('[data-variant-id]');
      if (idField) idField.value = variant.id;

      this.updateBundlePrices();
      this.updateStockBadge(variant);
      this.updateCountryCard(variant);

      if (variant.featured_media?.id) {
        this.showMedia(variant.featured_media.id);
      }
    }

    updateStockBadge(variant) {
      const badge = this.root.querySelector('[data-stock-badge]');
      const textNode = this.root.querySelector('[data-stock-text]');
      if (!badge || !textNode || !variant) return;

      const stockTemplate = this.root.dataset.stockTemplate || 'Only [count] left in stock!';
      const inStockText = this.root.dataset.inStockText || 'In stock and ready to order';
      const soldOutText = this.root.dataset.stockSoldoutText || 'Sold out';
      const qty = Number(variant.inventory_quantity || 0);
      const tracked = Boolean(variant.inventory_management);

      if (!variant.available) {
        textNode.textContent = soldOutText;
        badge.classList.add('is-sold-out');
        return;
      }

      badge.classList.remove('is-sold-out');

      if (tracked && qty > 0) {
        textNode.textContent = stockTemplate.includes('[count]')
          ? stockTemplate.replaceAll('[count]', String(qty))
          : `${stockTemplate} ${qty}`;
      } else {
        textNode.textContent = inStockText;
      }
    }

    updateCountryCard(variant) {
      const card = this.root.querySelector('[data-country-card]');
      if (!card) return;
      card.hidden = !variant?.available;
    }

    updateBundlePrices() {
      this.root.querySelectorAll('[data-bundle-card]').forEach(card => {
        const qty = Math.max(1, parseInt(card.dataset.bundleQty || '1', 10));
        const discount = Math.min(100, Math.max(0, parseFloat(card.dataset.bundleDiscount || '0')));
        const compare = this.getBundleCompareTotal(qty);
        const sale = Math.round(compare * (100 - discount) / 100);

        const priceNode = card.querySelector('[data-bundle-price]');
        const compareNode = card.querySelector('[data-bundle-compare]');
        const unitPriceNode = card.querySelector('[data-bundle-unit-price]');

        if (priceNode) priceNode.textContent = this.formatMoney(sale);
        if (compareNode) compareNode.textContent = this.formatMoney(compare);
        if (unitPriceNode) {
          unitPriceNode.textContent = `${this.formatMoney(Math.round(sale / qty))} / each`;
        }
      });
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
        }).format(Number(cents || 0) / 100);
      } catch (error) {
        return `${(Number(cents || 0) / 100).toFixed(2)} ${this.currency}`;
      }
    }

    getSelectedBundleCard() {
      return (
        this.root.querySelector('[data-bundle-card] input[type="radio"]:checked')?.closest('[data-bundle-card]') ||
        this.root.querySelector('[data-bundle-card].is-selected') ||
        this.root.querySelector('[data-bundle-card]')
      );
    }

    getSelectedBundlePricing() {
      const card = this.getSelectedBundleCard();

      if (!card) {
        const qty = Math.max(1, this.bundleQty || 1);
        const compare = this.getBundleCompareTotal(qty);

        return {
          qty,
          discount: 0,
          compare,
          sale: compare,
        };
      }

      const qty = Math.max(1, parseInt(card.dataset.bundleQty || String(this.bundleQty || 1), 10));
      const discount = Math.min(100, Math.max(0, parseFloat(card.dataset.bundleDiscount || '0')));
      const compare = this.getBundleCompareTotal(qty);
      const sale = Math.round(compare * (100 - discount) / 100);

      return { qty, discount, compare, sale };
    }

    updateStickyBar() {
      const bar = this.root.querySelector('[data-sticky-bar]');
      const selectionNode = this.root.querySelector('[data-sticky-selection]');
      const compareNode = this.root.querySelector('[data-sticky-compare]');
      const saleNode = this.root.querySelector('[data-sticky-sale]');
      const discountNode = this.root.querySelector('[data-sticky-discount]');
      const button = this.root.querySelector('[data-sticky-button]');
      const buttonText = this.root.querySelector('[data-sticky-button-text]');

      if (!bar || !selectionNode || !compareNode || !saleNode || !discountNode || !button || !buttonText) return;

      const selectedTotal = this.selection.length;
      const remaining = Math.max(0, this.bundleQty - selectedTotal);
      const { discount, compare, sale } = this.getSelectedBundlePricing();

      const selectedWord = this.root.dataset.stickySelectedText || 'selected';
      const discountTemplate = this.root.dataset.stickyDiscountText || '[count]% off';

      selectionNode.textContent = `${selectedTotal}/${this.bundleQty} ${selectedWord}`;
      compareNode.textContent = this.formatMoney(compare);
      saleNode.textContent = this.formatMoney(sale);

      if (discount > 0) {
        discountNode.hidden = false;
        discountNode.textContent = discountTemplate.replaceAll('[count]', String(discount));
      } else {
        discountNode.hidden = true;
      }

      const anyAvailable = this.variants.some(variant => variant.available);

      if (!anyAvailable) {
        button.disabled = true;
        buttonText.textContent =
          this.root.dataset.stickySoldoutText ||
          this.root.querySelector('[data-add-button-text]')?.dataset.soldText ||
          'Sold out';
        return;
      }

      button.disabled = false;

      if (remaining > 0) {
        const chooseTemplate = this.root.dataset.stickyChooseText || 'Choose [count] [shape]';
        const singular = this.root.dataset.stickyShapeSingular || 'shape';
        const plural = this.root.dataset.stickyShapePlural || 'shapes';
        const shapeWord = remaining === 1 ? singular : plural;

        buttonText.textContent = chooseTemplate
          .replaceAll('[count]', String(remaining))
          .replaceAll('[shape]', shapeWord);
      } else {
        buttonText.textContent =
          this.root.dataset.stickyAddText ||
          this.root.querySelector('[data-add-button-text]')?.dataset.addText ||
          'Add to cart';
      }
    }

    isCartDrawerOpen() {
      const selectors = [
        'cart-drawer',
        'modal-cart',
        '#CartDrawer',
        '#cart-drawer',
        '[data-cart-drawer]',
        '[data-modal-cart]',
        '.cart-drawer',
        '.modal-cart',
        '[x-show*="cart"]',
        '[x-show*="Cart"]',
      ];

      const candidates = [...document.querySelectorAll(selectors.join(','))];

      return candidates.some(element => {
        if (!element || element === this.root) return false;
        if (element.hidden || element.getAttribute('aria-hidden') === 'true') return false;

        const classList = element.classList;
        const explicitlyOpen =
          element.hasAttribute('open') ||
          element.getAttribute('aria-hidden') === 'false' ||
          element.getAttribute('data-state') === 'open' ||
          classList.contains('is-open') ||
          classList.contains('open') ||
          classList.contains('active') ||
          classList.contains('is-active');

        if (explicitlyOpen) return true;

        const openChild = element.querySelector(
          'dialog[open], [aria-modal="true"]:not([aria-hidden="true"]), [data-state="open"], .is-open, .is-active'
        );

        if (openChild) return true;

        const xShow = (
          element.getAttribute('x-show') ||
          element.getAttribute('x-bind:class') ||
          ''
        ).toLowerCase();

        if (!xShow.includes('cart')) return false;

        const style = window.getComputedStyle(element);
        if (
          style.display === 'none' ||
          style.visibility === 'hidden' ||
          Number.parseFloat(style.opacity || '1') === 0
        ) {
          return false;
        }

        const rect = element.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      });
    }

    bindStickyBar() {
      const bar = this.root.querySelector('[data-sticky-bar]');
      const button = this.root.querySelector('[data-sticky-button]');
      if (!bar || !button) return;

      button.addEventListener('click', event => {
        event.preventDefault();

        if (this.selection.length !== this.bundleQty) {
          const picker = this.root.querySelector('[data-variant-picker]');
          picker?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

        const form = this.root.querySelector('[data-product-form]');
        if (!form) return;

        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      });

      const footerCandidates = [
        ...document.querySelectorAll(
          'footer, #shopify-section-footer, .shopify-section-group-footer-group, [data-section-type="footer"]'
        ),
      ];

      const footer = footerCandidates.length
        ? footerCandidates[footerCandidates.length - 1]
        : null;

      let ticking = false;

      const updateStickyVisibility = () => {
        ticking = false;

        const rootRect = this.root.getBoundingClientRect();
        const rootTop = window.scrollY + rootRect.top;
        const scrollThreshold = rootTop + Math.min(180, window.innerHeight * 0.18);
        const hasScrolledEnough = window.scrollY > scrollThreshold;

        let footerReached = false;

        if (footer) {
          const footerRect = footer.getBoundingClientRect();
          footerReached =
            footerRect.top <= window.innerHeight &&
            footerRect.bottom >= 0;
        }

        const detectedCartDrawerOpen = this.isCartDrawerOpen();

        if (detectedCartDrawerOpen) {
          this.stickyCartDrawerOpen = true;
        } else if (Date.now() > this.stickyCartDrawerGraceUntil) {
          this.stickyCartDrawerOpen = false;
        }

        const shouldShow =
          hasScrolledEnough &&
          !footerReached &&
          !this.stickyCartDrawerOpen;

        bar.classList.toggle('is-visible', shouldShow);
        bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      };

      const scheduleStickyVisibility = () => {
        if (ticking) return;

        ticking = true;
        window.requestAnimationFrame(updateStickyVisibility);
      };

      const forceCartDrawerOpen = () => {
        this.stickyCartDrawerOpen = true;
        this.stickyCartDrawerGraceUntil = Date.now() + 900;
        scheduleStickyVisibility();

        // Give Alpine / the theme enough time to update x-show / drawer state.
        window.setTimeout(scheduleStickyVisibility, 60);
        window.setTimeout(scheduleStickyVisibility, 320);
      };

      const recheckCartDrawer = () => {
        window.setTimeout(() => {
          this.stickyCartDrawerGraceUntil = 0;
          this.stickyCartDrawerOpen = this.isCartDrawerOpen();
          scheduleStickyVisibility();
        }, 80);
      };

      const handleDocumentClick = event => {
        const trigger = event.target?.closest?.('button, a, [role="button"]');
        if (!trigger) return;

        const attributes = trigger
          .getAttributeNames()
          .map(name => `${name}=${trigger.getAttribute(name) || ''}`)
          .join(' ')
          .toLowerCase();

        const href = (trigger.getAttribute('href') || '').toLowerCase();
        const ariaControls = (trigger.getAttribute('aria-controls') || '').toLowerCase();

        const looksLikeCartOpen =
          (
            attributes.includes('cart') &&
            (
              attributes.includes('modal') ||
              attributes.includes('drawer') ||
              attributes.includes('open')
            )
          ) ||
          ariaControls.includes('cart') ||
          href === '/cart' ||
          href.endsWith('/cart');

        if (looksLikeCartOpen) {
          forceCartDrawerOpen();
          return;
        }

        const cartContainer = trigger.closest(
          'cart-drawer, modal-cart, #CartDrawer, #cart-drawer, [data-cart-drawer], [data-modal-cart], .cart-drawer, .modal-cart, [x-show*="cart"], [x-show*="Cart"]'
        );

        const looksLikeClose =
          attributes.includes('close') ||
          (trigger.getAttribute('aria-label') || '').toLowerCase().includes('close') ||
          (trigger.getAttribute('title') || '').toLowerCase().includes('close');

        if (cartContainer && looksLikeClose) {
          this.stickyCartDrawerOpen = false;
          recheckCartDrawer();
        }
      };

      const handleEscape = event => {
        if (event.key !== 'Escape') return;
        if (!this.stickyCartDrawerOpen) return;

        this.stickyCartDrawerOpen = false;
        recheckCartDrawer();
      };

      const handleAfterAdd = () => {
        forceCartDrawerOpen();
      };

      const handlePossibleClose = () => {
        recheckCartDrawer();
      };

      window.addEventListener('scroll', scheduleStickyVisibility, { passive: true });
      window.addEventListener('resize', scheduleStickyVisibility, { passive: true });

      window.addEventListener('shapes:modalcart:afteradditem', handleAfterAdd);
      document.body?.addEventListener('shapes:modalcart:afteradditem', handleAfterAdd);
      window.addEventListener('shapes:cart:afteradditem', handleAfterAdd);

      window.addEventListener('shapes:modalcart:close', handlePossibleClose);
      window.addEventListener('shapes:modalcart:closed', handlePossibleClose);
      window.addEventListener('shapes:cart:close', handlePossibleClose);
      window.addEventListener('shapes:cart:closed', handlePossibleClose);

      document.addEventListener('click', handleDocumentClick, true);
      document.addEventListener('keydown', handleEscape, true);

      this.stickyObserver?.disconnect();
      this.stickyObserver = new MutationObserver(scheduleStickyVisibility);
      this.stickyObserver.observe(document.body || document.documentElement, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: [
          'class',
          'style',
          'hidden',
          'open',
          'aria-hidden',
          'data-state',
        ],
      });

      this.stickyVisibilityCleanup = () => {
        window.removeEventListener('scroll', scheduleStickyVisibility);
        window.removeEventListener('resize', scheduleStickyVisibility);

        window.removeEventListener('shapes:modalcart:afteradditem', handleAfterAdd);
        document.body?.removeEventListener('shapes:modalcart:afteradditem', handleAfterAdd);
        window.removeEventListener('shapes:cart:afteradditem', handleAfterAdd);

        window.removeEventListener('shapes:modalcart:close', handlePossibleClose);
        window.removeEventListener('shapes:modalcart:closed', handlePossibleClose);
        window.removeEventListener('shapes:cart:close', handlePossibleClose);
        window.removeEventListener('shapes:cart:closed', handlePossibleClose);

        document.removeEventListener('click', handleDocumentClick, true);
        document.removeEventListener('keydown', handleEscape, true);

        this.stickyObserver?.disconnect();
        this.stickyObserver = null;
      };

      this.stickyCartDrawerOpen = this.isCartDrawerOpen();
      updateStickyVisibility();
    }

    bindReviewScroll() {
      const link = this.root.querySelector('[data-review-link]');
      if (!link) return;

      link.addEventListener('click', event => {
        const href = link.getAttribute('href') || '';
        const id = href.startsWith('#') ? href.slice(1) : href;
        if (!id) return;

        const target = document.getElementById(id);
        if (!target) return;

        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });

        if (history.replaceState) {
          history.replaceState(null, '', `#${id}`);
        }
      });
    }

    bindViewingCounter() {
      const pill = this.root.querySelector('[data-viewing-pill]');
      const textNode = this.root.querySelector('[data-viewing-text]');
      if (!pill || !textNode) return;

      this.renderViewerCount(textNode);
      this.scheduleViewerUpdate(textNode);
    }

    renderViewerCount(textNode) {
      const rawMin = parseInt(this.root.dataset.viewingMin || '17', 10);
      const rawMax = parseInt(this.root.dataset.viewingMax || '28', 10);
      const min = Math.max(1, Math.min(rawMin, rawMax));
      const max = Math.max(min, Math.max(rawMin, rawMax));

      let count = Math.floor(Math.random() * (max - min + 1)) + min;

      if (max > min && count === this.lastViewerCount) {
        count = count === max ? min : count + 1;
      }

      this.lastViewerCount = count;

      const template = this.root.dataset.viewingTemplate || '21 people are viewing this now';

      textNode.textContent = /\d[\d.,]*/.test(template)
        ? template.replace(/\d[\d.,]*/, String(count))
        : `${count} ${template}`;
    }

    scheduleViewerUpdate(textNode) {
      clearTimeout(this.viewingTimer);

      const delay = 7000 + Math.floor(Math.random() * 1001);

      this.viewingTimer = window.setTimeout(() => {
        this.renderViewerCount(textNode);
        this.scheduleViewerUpdate(textNode);
      }, delay);
    }

    buildCartItems() {
      const counts = this.getSelectionCounts();
      const bundleProperty = this.root.querySelector('[data-bundle-property]');
      const bundleTitle = bundleProperty?.value || `${this.bundleQty}-pack`;

      return [...counts.entries()].map(([variantId, quantity]) => ({
        id: Number(variantId),
        quantity,
        properties: {
          _Bundle: bundleTitle,
        },
      }));
    }

    bindForm() {
      const form = this.root.querySelector('[data-product-form]');
      if (!form) return;

      form.addEventListener('submit', async event => {
        event.preventDefault();

        const checkedBundleInput = this.root.querySelector(
          '[data-bundle-card] input[type="radio"]:checked'
        );

        const checkedBundleCard = checkedBundleInput?.closest('[data-bundle-card]');

        if (checkedBundleCard) {
          this.selectBundle(checkedBundleCard);
        }

        if (this.selection.length !== this.bundleQty) {
          this.updateSelectionUI();
          return;
        }

        const button = this.root.querySelector('[data-add-button]');
        const buttonText = this.root.querySelector('[data-add-button-text]');

        if (!button || button.disabled) return;

        const normalText = buttonText?.dataset.addText || 'Add to cart';

        button.disabled = true;
        button.setAttribute('aria-busy', 'true');

        if (buttonText) {
          buttonText.textContent = 'Adding...';
        }

        let addedSuccessfully = false;

        try {
          const items = this.buildCartItems();

          if (!items.length) {
            throw new Error('Please choose the products in your bundle first.');
          }

          const rootUrl = window.Shopify?.routes?.root || '/';

          const payload = {
            items,
            sections: 'cart-items,cart-footer,cart-item-count,cart-live-region',
            sections_url: window.location.pathname,
          };

          const response = await fetch(`${rootUrl}cart/add.js`, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            credentials: 'same-origin',
            body: JSON.stringify(payload),
          });

          const data = await response.json();

          if (!response.ok) {
            document.body.dispatchEvent(
              new CustomEvent('shapes:cart:adderror', {
                bubbles: true,
                detail: {
                  source: 'product-form',
                  sourceId: form.getAttribute('id') || '',
                  errors: data,
                },
              })
            );

            throw new Error(
              data?.description ||
              data?.message ||
              'Unable to add this bundle to the cart.'
            );
          }

          if (!data.sections) {
            throw new Error('Cart sections could not be refreshed.');
          }

          document.body.dispatchEvent(
            new CustomEvent('shapes:modalcart:afteradditem', {
              bubbles: true,
              detail: {
                response: data,
                forceOpen: true,
              },
            })
          );

          addedSuccessfully = true;

          if (buttonText) {
            buttonText.textContent = 'Added!';
          }

          window.setTimeout(() => {
            this.updateSelectionUI();
          }, 900);
        } catch (error) {
          console.error('[FunnyProductPage] Add to cart error:', error);

          if (buttonText) {
            buttonText.textContent = normalText;
          }

          window.alert(
            error?.message ||
            'Unable to add this bundle to the cart.'
          );
        } finally {
          button.removeAttribute('aria-busy');

          if (addedSuccessfully) {
            button.disabled = true;
          } else {
            this.updateSelectionUI();
          }
        }
      });
    }

    destroy() {
      clearTimeout(this.viewingTimer);
      clearTimeout(this.selectionErrorTimer);
      this.stickyVisibilityCleanup?.();
      this.stickyVisibilityCleanup = null;
    }
  }

  const init = scope => {
    const roots = scope.matches?.(ROOT_SELECTOR)
      ? [scope]
      : [...scope.querySelectorAll?.(ROOT_SELECTOR) || []];

    roots.forEach(root => {
      if (root.dataset.fppReady === 'true') return;
      root.dataset.fppReady = 'true';
      root._funnyProductPage = new FunnyProductPage(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', event => init(event.target));

  document.addEventListener('shopify:section:unload', event => {
    const root = event.target.matches?.(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector?.(ROOT_SELECTOR);

    root?._funnyProductPage?.destroy?.();
  });
})();
